import React from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { BaseView, BaseText } from '@/components/base'
import { FixedBottomButton, LabelAmount, AppButton } from '@/components'
import { getCoinGif } from '@/constants/coinGifs'
import useOffsets from '@/hooks/useOffsets'
import type { CryptoCode } from '@/constants/currencyCodes'
import { useAppAnalytics } from '@/services/analytics'
import { useEffect } from 'react'

export interface ConversionSuccessParams {
  title: string
  subtitle?: string
  description?: string
  amount: string
  code: CryptoCode
  label?: string
  relativeFiatFormatted?: string
  shouldMaskStablecoinAsFiat?: boolean
  doneRoute?: string
  navigationMethod?: 'dismiss' | 'replace'
}

export const ConversionSuccessScreen: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const offsets = useOffsets()
  const { trackEvent, events } = useAppAnalytics()

  // Get success data from location state
  const successData = location.state as ConversionSuccessParams | undefined

  useEffect(() => {
    if (successData) {
      trackEvent(events.MONEY_ACTION_COMPLETED, {
        action_type: 'convert',
        amount: parseFloat(successData.amount),
        currency: successData.code,
        // from_asset/to_asset could be derived if passed in params, skipping for now as optional
      })
      trackEvent(events.CRYPTO_ACTION_COMPLETED, {
        action_type: 'convert',
        asset_symbol: successData.code,
        amount: parseFloat(successData.amount),
        currency: successData.code, // simplified
      })
    }
  }, [successData, trackEvent, events.MONEY_ACTION_COMPLETED, events.CRYPTO_ACTION_COMPLETED])

  if (!successData) {
    return (
      <BaseView
        className="flex-1 justify-center items-center p-6"
        style={{ backgroundColor: '#1F2937' }}
      >
        <BaseText
          className="text-center text-lg font-medium"
          style={{ color: '#EF4444' }} // red-500
        >
          {'No transaction data found'}
        </BaseText>
        <AppButton
          label={'Go Home'}
          onPress={() => navigate('/dashboard')}
          className="mt-6"
          type="primaryGradient"
        />
      </BaseView>
    )
  }

  const handleDone = () => {
    if (successData.doneRoute) {
      if (successData.navigationMethod === 'dismiss') {
        // For dismiss, we'll use replace as we don't have dismiss functionality in web
        navigate(successData.doneRoute, { replace: true })
      } else {
        navigate(successData.doneRoute, { replace: true })
      }
    } else {
      // Default navigation to activity page (similar to React Native)
      navigate('/activity', { replace: true })
    }
  }

  return (
    <BaseView
      className="flex-1"
      px={13}
      style={{
        backgroundColor: '#141417',
        height: '100vh',
        paddingTop: offsets.top + 30,
      }}
    >
      <BaseView
        className="flex-1 items-center px-6"
        style={{
          paddingBottom: '80px',
        }}
      >
        {/* Currency GIF */}
        <div className="mb-12">
          <img
            src={getCoinGif(successData.code)}
            alt={successData.code}
            style={{
              width: '200px',
              height: '200px',
              objectFit: 'contain',
            }}
          />
        </div>

        {/* Content */}
        <BaseView className="text-center mb-12" style={{ gap: '20px' }}>
          {/* Title */}
          <BaseText
            fontFamily="medium"
            className="text-center"
            style={{ fontSize: '28px' }}
            color="label.default"
          >
            {successData.title}
          </BaseText>

          {/* Subtitle */}
          {successData.subtitle && (
            <BaseText
              fontFamily="medium"
              className="text-center"
              style={{ fontSize: '22px' }}
              color="label.description"
            >
              {successData.subtitle}
            </BaseText>
          )}

          {/* Amount and Conversion */}
          <BaseView
            className="flex flex-col items-center"
            style={{ gap: '16px' }}
          >
            <LabelAmount
              style={{
                whiteSpace: 'nowrap',
              }}
              amount={successData.amount}
              currencyCode={successData.code}
              currencyLabel={successData.label}
              shouldMaskStablecoinAsFiat={
                successData.shouldMaskStablecoinAsFiat
              }
            />

            {successData.relativeFiatFormatted && (
              <BaseText className="text-center" style={{ color: '#9CA3AF' }}>
                ≈{successData.relativeFiatFormatted}
              </BaseText>
            )}
          </BaseView>

          {/* Description */}
          {successData.description && (
            <BaseText
              className="text-xl text-center"
              style={{ color: '#9CA3AF' }}
            >
              {successData.description}
            </BaseText>
          )}
        </BaseView>
      </BaseView>

      {/* Done Button */}
      <FixedBottomButton
        buttons={{
          label: t('form:actions.done'),
          onPress: handleDone,
          type: 'secondaryGray',
        }}
      />
    </BaseView>
  )
}

export default ConversionSuccessScreen
